import { getEventTransfer } from 'slate-react';
import { FormatTypes } from '../../../../../../../../../constants/templateDictionaries';
import validateEmail from './pasteValidators/email';
import validatePhoneNumber from './pasteValidators/phoneNumber';
import validatePersonalLink from './pasteValidators/personalLink';
import validateScore from './pasteValidators/score';
import renderDict from './renderDict';
import pasteInSingleLine from './pasteInSingleLine';
import pasteMultipleLine from './pasteMultipleLine';
import { removeNonPrintableCharacters } from './removeNonPrintableCharacters';

export const SINGLE_LINE = 'SINGLE_LINE';
export const SINGLE_LINE_ORGANIZATION = 'SINGLE_LINE_ORGANIZATION';
export const MULTIPLE_LINE = 'MULTIPLE_LINE';
export const PHONE_NUMBER = 'PHONE_NUMBER';
export const EMAIL = 'EMAIL';
export const PERSONAL_LINK = 'PERSONAL_LINK';
export const SCORE_ORGANIZATION = 'SCORE_ORGANIZATION';
export const DATE = 'DATE';
export const LIST_ITEM = 'LIST_ITEM';
export const ARRAY_ITEM = 'ARRAY_ITEM';

export const isSnackBarAlert = ({ isNotValid, setSnackBar }) => {
  if (isNotValid) {
    setSnackBar({
      snackBarMessage: 'Please paste a valid data',
      snackBarType: 'error',
    });
    return true;
  }
  return false;
};

export const pasteTypes = {
  [FormatTypes.RESUME]: {
    full_name: SINGLE_LINE,
    tagline: SINGLE_LINE,
    email: EMAIL,
    address: SINGLE_LINE,
    objective: SINGLE_LINE,
    phone_number: PHONE_NUMBER,
    personal_link: PERSONAL_LINK,
    skill_organization: SINGLE_LINE_ORGANIZATION,
    summary: MULTIPLE_LINE,
    organization_name: SINGLE_LINE_ORGANIZATION,
    organization_address: SINGLE_LINE_ORGANIZATION,
    organization_role_title: SINGLE_LINE_ORGANIZATION,
    organization_role_score_min: SCORE_ORGANIZATION,
    organization_role_score_max: SCORE_ORGANIZATION,
    organization_role_date: DATE,
    organization_role_editable_div: MULTIPLE_LINE,
    list_item: LIST_ITEM,
    hobbies_and_interests: ARRAY_ITEM,
    language: ARRAY_ITEM,
    publication: ARRAY_ITEM,
  },

  [FormatTypes.COVER_LETTER]: {
    sender_name: SINGLE_LINE,
    sender_phone_number: PHONE_NUMBER,
    sender_tagline: SINGLE_LINE,
    sender_address: SINGLE_LINE,
    sender_email: EMAIL,
    receiver_name: SINGLE_LINE,
    receiver_tagline: SINGLE_LINE,
    receiver_organization: SINGLE_LINE,
    receiver_address: SINGLE_LINE,
    letter_date: DATE,
    letter_body: MULTIPLE_LINE,
    open_greeting: SINGLE_LINE,
    subject_line: SINGLE_LINE,
    close_greeting: SINGLE_LINE,
    personal_link: PERSONAL_LINK,
    list_item: LIST_ITEM,
  },
};

export const commonOrgBlocks = [
  'organization_name',
  'organization_address',
  'organization_role_title',
  'organization_role_score_min',
  'organization_role_score_max',
  'organization_role_date',
  'array-item',
];

export default (params) => {
  const { event, editor, formatType, setSnackBar } = params;

  const transfer = getEventTransfer(event);
  let text = null;

  if (transfer?.type !== 'fragment') {
    const { clipboardData } = event;
    text = clipboardData?.getData('text').replace(/\r/gi, '');
    text = removeNonPrintableCharacters(text);
  }
  /**
   * Fetch paste type
   */
  const {
    value: { document, focusBlock },
  } = editor;

  const { type: focusBlockType, key: focusBlockKey } = focusBlock;
  const { nodes: documentNodes } = document;
  const { type: firstBlockType } = documentNodes?.toArray()[0] || {};

  const commonFocusBlockType = renderDict[focusBlockType];
  let pasteType;
  if (focusBlockType === 'paragraph') {
    /**
     * CASE 1: Paste in Objective section which will not accept bullets
     */
    const { type: parentType } = document.getParent(focusBlockKey);

    pasteType =
      pasteTypes[formatType][parentType] ||
      pasteTypes[formatType][renderDict[parentType]];
  } else if (commonOrgBlocks.includes(commonFocusBlockType)) {
    pasteType = pasteTypes[formatType][commonFocusBlockType];
  } else {
    pasteType = pasteTypes[formatType][firstBlockType];
  }

  /**
   * CASE 1: Copy from external source
   */
  if (text) {
    /**
     * CASE 1a: Paste in single line inputs
     */
    if (pasteType === SINGLE_LINE || pasteType === SINGLE_LINE_ORGANIZATION) {
      const singleLineText = text?.replace(/\n/gi, ' ');
      editor.withoutNormalizing(() => editor.insertText(singleLineText));
      return true;
    }

    /**
     * CASE 1b: Paste in phone number inputs
     */
    if (pasteType === PHONE_NUMBER) {
      const isNotValid = validatePhoneNumber(text);
      if (!isNotValid) {
        editor?.withoutNormalizing(() => editor?.insertText(text));
        return true;
      }
      return isSnackBarAlert({
        isNotValid,
        setSnackBar,
      });
    }

    /**
     * CASE 1c: Paste in email inputs
     */
    if (pasteType === EMAIL) {
      const isNotValid = validateEmail(text);
      return isSnackBarAlert({
        isNotValid,
        setSnackBar,
      });
    }

    /**
     * CASE 1d: Paste in personal links inputs
     */
    if (pasteType === PERSONAL_LINK) {
      const isNotValid = validatePersonalLink(text);
      if (isNotValid)
        return isSnackBarAlert({
          isNotValid,
          setSnackBar,
        });
    }

    /**
     * CASE 1e: Paste in custom node score inputs
     */
    if (pasteType === SCORE_ORGANIZATION) {
      const isNotValid = validateScore(text);
      return isSnackBarAlert({
        isNotValid,
        setSnackBar,
      });
    }

    /**
     * CASE 1f: Paste in custom node date inputs
     */
    if (pasteType === DATE) {
      event.preventDefault();
      return true;
    }

    /**
     * CASE 1g: Paste in custom node role editable div inputs
     */
    if (pasteType === LIST_ITEM) {
      editor?.withoutNormalizing(() => editor?.insertText(text));
      return true;
    }

    /**
     * CASE 1h: Paste in custom node role array item inputs
     */
    if (pasteType === ARRAY_ITEM) {
      const singleLineText = text?.replace(/\n/gi, ' ');
      editor?.insertText(singleLineText);
      return true;
    }

    editor?.insertText(text);
    return true;
  }

  /**
   * CASE 2: If the copy/cut is from the internal app
   */
  const { fragment } = transfer;
  const copiedFragment = fragment;

  /**
   * CASE 2a: Paste in single line inputs
   */
  if (
    pasteType === SINGLE_LINE ||
    pasteType === ARRAY_ITEM ||
    pasteType === SINGLE_LINE_ORGANIZATION
  ) {
    pasteInSingleLine({
      editor,
      copiedFragment,
      pasteType,
    });
    return true;
  }

  /**
   * CASE 2a: Paste in email inputs
   */
  if (pasteType === EMAIL) {
    const textCopied = copiedFragment?.text;
    const isNotValid = validateEmail(textCopied);
    const isAlert = isSnackBarAlert({
      isNotValid,
      setSnackBar,
    });
    if (isAlert) return true;

    pasteInSingleLine({
      editor,
      copiedFragment,
      pasteType,
    });
    /**
     * Santize email text
     */
    return true;
  }

  if (pasteType === PHONE_NUMBER) {
    const textCopied = copiedFragment?.text;
    const isNotValid = validatePhoneNumber(textCopied);
    if (!isNotValid) {
      pasteInSingleLine({
        editor,
        copiedFragment,
        pasteType,
      });
      return true;
    }
    isSnackBarAlert({
      isNotValid,
      setSnackBar,
    });
    return true;
  }

  if (pasteType === SCORE_ORGANIZATION) {
    const textCopied = copiedFragment?.text;
    const isNotValid = validateScore(textCopied);
    const isAlert = isSnackBarAlert({ isNotValid, setSnackBar });
    if (isAlert) return true;

    pasteInSingleLine({ editor, copiedFragment, pasteType });
    /**
     * Santize phone number text
     */
    return true;
  }

  if (pasteType === PERSONAL_LINK) {
    const textCopied = copiedFragment?.text;
    const isNotValid = validatePersonalLink(textCopied);
    const isAlert = isSnackBarAlert({
      isNotValid,
      setSnackBar,
    });
    if (isAlert) return true;

    pasteInSingleLine({
      editor,
      copiedFragment,
      pasteType,
    });
    /**
     * Santize personalLinks text
     */
    return true;
  }

  if (pasteType === MULTIPLE_LINE || pasteType === LIST_ITEM) {
    return pasteMultipleLine({ copiedFragment, editor });
  }

  return false;
};
